import { buildEnterpriseReport, type EnterpriseActionInput, type EnterpriseCardInput, type EnterpriseKaizenInput, type EnterpriseProblemInput } from '../reports/enterprise/enterpriseReportModel';
import { renderEnterprisePdf } from '../reports/enterprise/enterpriseReportPdf';
import { getFullContentDimensions, prepareClonedDocumentForExport, prepareElementsForExport } from './exportHelpers';

interface ProjectData {
  name: string;
  organizationName?: string | null;
  actions?: EnterpriseActionInput[];
  problems?: EnterpriseProblemInput[];
  kaizens?: EnterpriseKaizenInput[];
  annualCostSaved?: number | null;
  kaizenCards?: EnterpriseCardInput[] | null;
  futureCards?: EnterpriseCardInput[] | null;
}

export async function captureFlowCanvas(canvasElement: HTMLElement): Promise<{ dataUrl: string; widthPx: number; heightPx: number } | null> {
  const { default: html2canvas } = await import('html2canvas');
  const { width, height } = getFullContentDimensions(canvasElement);
  if (width <= 0 || height <= 0) return null;
  const canvas = await html2canvas(canvasElement, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    logging: false,
    allowTaint: true,
    scrollX: 0,
    scrollY: 0,
    x: 0,
    y: 0,
    width,
    height,
    windowWidth: width,
    windowHeight: height,
    onclone: (clonedDoc) => {
      prepareClonedDocumentForExport(clonedDoc, 'canvas-content');
      const cloned = clonedDoc.querySelector('#canvas-content') as HTMLElement | null;
      if (!cloned) return;
      cloned.style.transform = 'none';
      cloned.style.width = `${width}px`;
      cloned.style.height = `${height}px`;
      prepareElementsForExport(cloned);
    },
  });
  if (canvas.width <= 0 || canvas.height <= 0) return null;
  return { dataUrl: canvas.toDataURL('image/png', 1.0), widthPx: width, heightPx: height };
}

export const exportEnterpriseReport = async (
  canvasElement: HTMLElement,
  project: ProjectData,
  cards: EnterpriseCardInput[],
): Promise<void> => {
  const model = buildEnterpriseReport({
    processName: project.name,
    organizationName: project.organizationName,
    cards,
    actions: project.actions,
    problems: project.problems,
    kaizens: project.kaizens,
    annualCostSaved: project.annualCostSaved,
    kaizenCards: project.kaizenCards,
    futureCards: project.futureCards,
  });

  let map: { dataUrl: string; widthPx: number; heightPx: number } | null = null;
  try {
    map = await captureFlowCanvas(canvasElement);
  } catch (error) {
    console.warn('[enterprise-report] Mapa não capturado. O diagnóstico segue sem a imagem.', error);
  }

  await renderEnterprisePdf(model, map);
};
